import _ from 'lodash';
import { ofType, type ActionsObservable } from 'redux-observable';
import { forkJoin, of } from 'rxjs';
import { catchError, delay, map, mergeMap } from 'rxjs/operators';
import type { LoadHeatmaps } from './heatmap.actions';
import { HeatmapActionsTypes, LoadHeatmapsFailure, LoadHeatmapsSuccess, ReloadHeatmaps } from './heatmap.actions';
import { defaultRequest } from './heatmap.models';
import { getHeatmaps } from './heatmap.service';

export const handleReloadHeatmaps = (action$: ActionsObservable<ReturnType<typeof ReloadHeatmaps>>) =>
  action$.pipe(
    ofType(HeatmapActionsTypes.RELOAD_HEATMAPS),
    map(action => action.payload),
    mergeMap(data => {
      const requests = data.request.areas.map(area => {
        const request = {
          ...defaultRequest,
          property_id: data.request.property_id,
          indicators: [...data.request.indicators],
          scouting_windows: [...data.request.scouting_windows],
          area_id: area,
          options: data.request.options ? { ...defaultRequest.options, ...data.request.options } : defaultRequest.options,
          colorization: data.request.colorization ?? defaultRequest.colorization,
          interpolation: data.request.interpolation ?? defaultRequest.interpolation
        };
        return getHeatmaps(request).pipe(
          map(response => {
            const heatmap = response.data;
            if (heatmap?.url) {
              return { [area]: heatmap.url };
            }
            return { [area]: '' };
          }),
          catchError(() => {
            return of({});
          })
        );
      });
      return forkJoin(requests).pipe(
        map(heatmaps => {
          const success = heatmaps.reduce((acc, url) => {
            if (!Object.values(url)[0]) {
              return false;
            }
            return acc;
          }, true);
          if (!success && data.retry <= 4) {
            return ReloadHeatmaps({ request: data.request, retry: data.retry + 1 });
          }
          return LoadHeatmapsSuccess({
            heatmaps: _.merge({}, ...heatmaps) as Record<string, string>,
            currentHeatmap: { phenomenon: data.request.phenomenon, indicator: data.request.indicator }
          });
        })
      );
    }),
    delay(1000),
    catchError(() => {
      return of(LoadHeatmapsFailure());
    })
  );

export const handleLoadHeatmaps = (action$: ActionsObservable<ReturnType<typeof LoadHeatmaps>>) =>
  action$.pipe(
    ofType(HeatmapActionsTypes.LOAD_HEATMAPS),
    map(action => action.payload),
    mergeMap(params => {
      const requests = params.areas.map(area => {
        const request = {
          ...defaultRequest,
          property_id: params.property_id,
          indicators: [...params.indicators],
          scouting_windows: [...params.scouting_windows],
          area_id: area,
          options: params.options ? { ...defaultRequest.options, ...params.options } : defaultRequest.options,
          colorization: params.colorization ?? defaultRequest.colorization,
          geometry_date: params.geometry_date,
          interpolation: params.interpolation ?? defaultRequest.interpolation
        };
        return getHeatmaps(request).pipe(
          map(response => {
            const heatmap = response.data;
            if (heatmap?.url) {
              return { [area]: heatmap.url };
            }
            return { [area]: '' };
          }),
          catchError(() => {
            return of({});
          })
        );
      });
      return forkJoin(requests).pipe(
        map(heatmaps => {
          const success = heatmaps.reduce((acc, url) => {
            if (!Object.values(url)[0]) {
              return false;
            }
            return acc;
          }, true);
          if (!success) {
            return ReloadHeatmaps({ request: params, retry: 1 });
          }
          return LoadHeatmapsSuccess({
            heatmaps: _.merge({}, ...heatmaps) as Record<string, string>,
            currentHeatmap: { phenomenon: params.phenomenon, indicator: params.indicator }
          });
        })
      );
    }),
    delay(1000),
    catchError(() => {
      return of(LoadHeatmapsFailure());
    })
  );
